// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useInstitutionForm } from '@client/banks/hooks/use-institution-form';
import type { InstitutionFormValues } from '@client/banks/hooks/use-institution-form';
import type { UpdateBankInstitution } from '@client/banks/types/institution';
import { buildUpdateInstitutionPayload } from '@client/banks/utils/build-update-advisory-accounts-citec-payload';
import { mapAdvisoryBankToUpdateInstitution } from '@client/banks/utils/map-advisory-bank-to-update-institution';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import type { AdvisoryBank } from '@shared/data-access/types/brokerage-types';
import { useCallback, useMemo } from 'react';

export const BANK_INSTITUTION_UPDATE_FORM_ID = 'bank-institution-update-form';

export interface BankInstitutionUpdateDrawerProps {
  readonly institution: AdvisoryBank | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDelete?: () => void;
}

export const useBankInstitutionUpdateDrawer = ({
  institution,
  open,
  onClose,
  onDelete,
}: BankInstitutionUpdateDrawerProps) => {
  const defaultValues = useMemo(
    () =>
      institution
        ? mapAdvisoryBankToUpdateInstitution(institution)
        : { id: '', name: '', logoUrl: '' },
    [institution],
  );

  const form = useInstitutionForm({
    variant: 'update',
    defaultValues,
    disabled: !open,
  });

  const [updateAccountsCitec, { isLoading: isSaveLoading }] =
    advisoryApi.useUpdateAdvisoryAccountsCitecMutation();

  const onSubmit = useCallback(
    async (data: InstitutionFormValues) => {
      if (!institution) {
        return;
      }

      const payload: UpdateBankInstitution = {
        id: institution.id,
        name: data.name,
        logoUrl: data.logoUrl,
      };

      await updateAccountsCitec(buildUpdateInstitutionPayload(payload)).unwrap();
      onClose();
    },
    [institution, onClose, updateAccountsCitec],
  );

  const onSave = useCallback(() => {
    void form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const isSaveDisabled = useMemo(
    () => !form.formState.isDirty || !form.formState.isValid,
    [form.formState.isDirty, form.formState.isValid],
  );

  return {
    institution,
    open,
    onClose,
    onDelete,
    form,
    onSubmit,
    onSave,
    isSaveDisabled,
    isSaveLoading,
  };
};
